import { type JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { Spinner } from '@/components/ui';
import {
  openImpersonatedTab,
  useCustomers,
  useImpersonate,
  type Customer,
} from '@/hooks/useAdminCustomers';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

/**
 * Tenant switcher — anchored button in the topbar that opens a dropdown
 * listing the reseller's own workspace plus all of its sub-tenant customers.
 * Clicking a customer triggers impersonation (a new tab opens with the
 * customer's session), so the reseller can jump into a customer's portal
 * without leaving their own admin tab.
 *
 * Lives next to the search input on the topbar but only renders when the
 * active session has tenant_type platform or reseller — customer-tenant
 * users have nothing to switch into.
 *
 * Closes on: outside click, escape key, route change inside the impersonate
 * helper (which opens a new tab — the original tab keeps its session, so
 * the dropdown closes here for cleanliness rather than navigation).
 */
// Stable empty array so useMemo's dependency comparison doesn't fire on
// every render when the customers query hasn't returned yet — without
// this, the `customers.data?.items ?? []` fallback creates a fresh array
// reference each render and React's useMemo invalidates on every paint.
const EMPTY_CUSTOMERS: Customer[] = [];

export function TenantSwitcher(): JSX.Element | null {
  const { t } = useTranslation('default');
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Only resellers / platform admins see the switcher. Customer-tenant
  // users have no sub-tenants to switch into; surfacing an empty dropdown
  // would just be noise.
  const isResellerOrPlatform =
    user?.tenantType === 'platform' || user?.tenantType === 'reseller';

  // Defer the customers query until the user opens the dropdown AND has a
  // session that can hit /v1/admin/customers — without the gate, customer-
  // tenant pages would spam 403s on every paint and resellers who never
  // open the switcher would still pay one round-trip per page load.
  const customers = useCustomers({ enabled: open && isResellerOrPlatform });
  const impersonate = useImpersonate();

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    // mouseup (not mousedown) to avoid racing with the toggle button's
    // own click handler — a mousedown listener would fire BEFORE React's
    // synthetic onClick in some browsers, closing the dropdown the same
    // tick the trigger tries to open it.
    window.addEventListener('keydown', onKey);
    window.addEventListener('mouseup', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mouseup', onClick);
    };
  }, [open]);

  const items: Customer[] = customers.data?.items ?? EMPTY_CUSTOMERS;
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const live = items.filter((c) => c.status !== 'closed');
    if (!q) return live;
    return live.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.owner_email ?? '').toLowerCase().includes(q),
    );
  }, [items, query]);

  if (!isResellerOrPlatform) return null;

  const onPickCustomer = async (c: Customer): Promise<void> => {
    try {
      const res = await impersonate.mutateAsync(c.id);
      openImpersonatedTab(res.access_token, res.customer_id, res.customer_name);
      setOpen(false);
    } catch {
      // Surfaced through the customers list refetch + a future global
      // toast layer; closing the dropdown isn't useful here because the
      // reseller may want to retry on the next row.
    }
  };

  const workspaceName = user?.name ?? user?.email ?? t('ts_workspace_fallback');
  // Reuse useAuth's pre-computed initial so the switcher avatar matches
  // the avatar pill on the right side of the topbar — both should show
  // the same letter for the same session.
  const initial = user?.initial ?? '?';

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-2 h-9 px-2.5 text-[13px]',
          'bg-surface-2 border border-line rounded-md text-text',
          'hover:bg-surface-3 transition-colors',
          open && 'bg-surface-3',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('ts_button_title')}
      >
        <span
          aria-hidden="true"
          className="w-6 h-6 rounded-md grid place-items-center text-volt-ink text-[11px] font-bold flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, var(--volt) 0%, var(--volt-600) 100%)' }}
        >
          {initial}
        </span>
        <span className="hidden md:inline truncate max-w-[160px]">{workspaceName}</span>
        <Icon.chevronDown width="14" height="14" className="text-text-3 flex-shrink-0" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('ts_button_title')}
          className={cn(
            'absolute top-full left-0 mt-2 w-[360px] z-50',
            'bg-surface border border-line rounded-lg overflow-hidden',
          )}
          style={{
            boxShadow:
              '0 16px 48px rgba(8, 14, 12, 0.14), 0 4px 12px rgba(8, 14, 12, 0.06)',
          }}
        >
          {/* Search */}
          <div className="px-3.5 pt-3 pb-2 border-b border-line-soft">
            <div className="text-[11px] uppercase tracking-[0.06em] text-text-3 font-medium mb-2">
              {t('ts_section_switch')}
            </div>
            <div className="flex items-center gap-2 px-2.5 py-1.5 bg-surface-2 border border-line rounded-md">
              <Icon.search width="14" height="14" className="text-text-4 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('ts_search_placeholder')}
                className="flex-1 border-none outline-none bg-transparent text-[13px] text-text placeholder:text-text-4"
                aria-label={t('ts_search_placeholder')}
              />
            </div>
          </div>

          {/* Self entry */}
          <div className="px-1.5 pt-2 pb-1">
            <div className="text-[10px] uppercase tracking-[0.06em] text-text-4 font-medium px-2.5 pt-1 pb-1.5">
              {t('ts_section_self')}
            </div>
            <SwitcherRow
              initial={initial}
              name={workspaceName}
              meta={t('ts_self_meta', { count: items.length })}
              selected
              badge={
                user?.tenantType === 'platform'
                  ? t('ts_badge_platform')
                  : t('ts_badge_reseller')
              }
              onClick={() => setOpen(false)}
            />
          </div>

          {/* Customers */}
          <div className="px-1.5 pt-1 pb-1.5 border-t border-line-soft">
            <div className="flex justify-between items-center px-2.5 pt-2 pb-1.5">
              <span className="text-[10px] uppercase tracking-[0.06em] text-text-4 font-medium">
                {t('ts_section_customers', { count: visible.length })}
              </span>
            </div>
            <div className="max-h-[280px] overflow-y-auto">
              {customers.isLoading ? (
                <div className="py-6 text-center">
                  <Spinner />
                </div>
              ) : visible.length === 0 ? (
                <div className="py-6 px-3 text-center text-[12px] text-text-4">
                  {query
                    ? t('ts_no_match', { query })
                    : t('ts_no_customers')}
                </div>
              ) : (
                visible.slice(0, 50).map((c) => (
                  <SwitcherRow
                    key={c.id}
                    initial={c.name.trim().charAt(0).toUpperCase() || '?'}
                    name={c.name}
                    meta={
                      <span>
                        {c.country && <span className="mono">{c.country}</span>}
                        {c.country && ' · '}
                        <span className="num">${c.balance.toFixed(2)}</span>
                      </span>
                    }
                    suspended={c.status === 'suspended'}
                    actionHint={t('ts_view_as')}
                    onClick={() => void onPickCustomer(c)}
                    disabled={impersonate.isPending}
                  />
                ))
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-2.5 py-2 border-t border-line-soft bg-surface-2 flex justify-between items-center">
            <Link
              to="/admin/customers"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 text-[12px] text-text-2 hover:text-text"
            >
              <Icon.plus width="12" height="12" />
              {t('ts_footer_new')}
            </Link>
            <Link
              to="/admin/customers"
              onClick={() => setOpen(false)}
              className="text-[12px] text-text-3 hover:text-text"
            >
              {t('ts_footer_manage')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

interface SwitcherRowProps {
  initial: string;
  name: string;
  meta: React.ReactNode;
  selected?: boolean;
  suspended?: boolean;
  actionHint?: string;
  badge?: string;
  disabled?: boolean;
  onClick?: () => void;
}
function SwitcherRow({
  initial,
  name,
  meta,
  selected = false,
  suspended = false,
  actionHint,
  badge,
  disabled = false,
  onClick,
}: SwitcherRowProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left',
        'transition-colors',
        selected ? 'bg-volt-50' : 'hover:bg-surface-2',
        disabled && 'opacity-50 cursor-not-allowed',
        suspended && 'opacity-70',
      )}
    >
      <span
        aria-hidden="true"
        className="w-7 h-7 rounded-md grid place-items-center text-white text-[11px] font-bold flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, var(--volt-500), var(--volt-700))' }}
      >
        {initial}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-text font-medium truncate">{name}</div>
        <div className="text-[11px] text-text-3 mt-0.5 truncate">{meta}</div>
      </div>
      {badge && (
        <span className="mono text-[9px] uppercase tracking-[0.04em] font-semibold px-1.5 py-0.5 bg-volt-100 text-volt-700 rounded">
          {badge}
        </span>
      )}
      {!badge && actionHint && (
        <span className="text-[11px] text-text-4 whitespace-nowrap">{actionHint}</span>
      )}
    </button>
  );
}
